import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AppLayout from '../../components/ui/AppLayout';
import HeaderBar from '../../components/ui/HeaderBar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import { biodataAPI, recruiterAPI, clabApplicationAPI } from '../../utils/api';
import { useBiodataCache } from '../../context/BiodataCacheContext';
import * as XLSX from 'xlsx';
import NationalityBreakdownTab from './components/NationalityBreakdownTab';
import WorkerDetailsModal from './components/WorkerDetailsModal';

const ClabReportsAnalytics = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const { getAllBiodatas } = useBiodataCache();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Analytics Data
  const [dashboardData, setDashboardData] = useState({
    recruiterSubmissions: [],
    nationalityBreakdown: [],
    monthlyTrends: [],
    statusSummary: {},
    totalWorkers: 0
  });
  
  // UI State
  const [activeTab, setActiveTab] = useState('recruiter'); // recruiter, nationality, monthly, export
  const [selectedPeriod, setSelectedPeriod] = useState('current'); // current, last3months, last6months, lastyear
  const [selectedRecruiter, setSelectedRecruiter] = useState(null);
  const [selectedNationality, setSelectedNationality] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [showWorkerDetails, setShowWorkerDetails] = useState(false);
  const [workerDetailsData, setWorkerDetailsData] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const breadcrumbItems = [
    { label: 'CLAB Dashboard', path: '/clab-dashboard' },
    { label: 'Reports & Analytics', path: '/clab-reports-analytics' }
  ];

  useEffect(() => {
    loadAnalyticsData();
  }, [selectedPeriod]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all biodata for analytics
      const biodataResult = await getAllBiodatas({ per_page: 10000 });
      if (biodataResult.fromCache) {
      }
      const recruitersResult = await recruiterAPI.getAllRecruiters();
      const applicationsResult = await clabApplicationAPI.getAllApplications();

      if (biodataResult.success && biodataResult.data) {
        const allBiodata = biodataResult.data.data || [];
        const recruiters = recruitersResult.success ? recruitersResult.data || [] : [];
        const applications = applicationsResult.status === 'success' ? applicationsResult.data.data || [] : [];

        // Filter by selected period
        const filteredBiodata = filterByPeriod(allBiodata);
        
        // Generate analytics
        const analytics = generateAnalytics(filteredBiodata, recruiters, applications);
        setDashboardData(analytics);
      }
    } catch (error) {
      console.error('Failed to load analytics:', error);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const filterByPeriod = (biodata) => {
    const now = new Date();
    let startDate;

    switch (selectedPeriod) {
      case 'last3months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1);
        break;
      case 'last6months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 6, 1);
        break;
      case 'lastyear':
        startDate = new Date(now.getFullYear() - 1, now.getMonth(), 1);
        break;
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
    }

    return biodata.filter(b => new Date(b.created_at) >= startDate);
  };

  const generateAnalytics = (biodata, recruiters, applications) => {
    // Recruiter Submissions Analytics
    const recruiterStats = {};
    biodata.forEach(b => {
      const recruiterId = b.recruiter_id;
      const recruiter = recruiters.find(r => r.id === recruiterId);
      const recruiterName = recruiter ? recruiter.agency_name : 'Unknown';
      const country = recruiter ? recruiter.country : 'Unknown';

      if (!recruiterStats[recruiterId]) {
        recruiterStats[recruiterId] = {
          id: recruiterId,
          name: recruiterName,
          country: country,
          totalSubmission: 0,
          approved: 0,
          rejected: 0,
          pending: 0,
          biodata: []
        };
      }

      recruiterStats[recruiterId].totalSubmission++;
      recruiterStats[recruiterId].biodata.push(b);
      
      // Count by status
      switch (b.status) {
        case 'approved':
          recruiterStats[recruiterId].approved++;
          break;
        case 'rejected':
          recruiterStats[recruiterId].rejected++;
          break;
        default:
          recruiterStats[recruiterId].pending++;
          break;
      }
    });

    // Nationality Breakdown
    const nationalityStats = {};
    biodata.forEach(b => {
      const nationality = b.nationality || 'Unknown';
      if (!nationalityStats[nationality]) {
        nationalityStats[nationality] = {
          nationality,
          total: 0,
          approved: 0,
          rejected: 0,
          pending: 0,
          biodata: []
        };
      }

      nationalityStats[nationality].total++;
      nationalityStats[nationality].biodata.push(b);
      
      switch (b.status) {
        case 'approved':
          nationalityStats[nationality].approved++;
          break;
        case 'rejected':
          nationalityStats[nationality].rejected++;
          break;
        default:
          nationalityStats[nationality].pending++;
          break;
      }
    });

    // Monthly Trends
    const monthlyStats = {};
    biodata.forEach(b => {
      const date = new Date(b.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

      if (!monthlyStats[monthKey]) {
        monthlyStats[monthKey] = {
          month: monthLabel,
          total: 0,
          approved: 0,
          rejected: 0,
          pending: 0,
          biodata: []
        };
      }

      monthlyStats[monthKey].total++;
      monthlyStats[monthKey].biodata.push(b);
      
      switch (b.status) {
        case 'approved':
          monthlyStats[monthKey].approved++;
          break;
        case 'rejected':
          monthlyStats[monthKey].rejected++;
          break;
        default:
          monthlyStats[monthKey].pending++;
          break;
      }
    });

    // Status Summary
    const statusSummary = {
      total: biodata.length,
      approved: biodata.filter(b => b.status === 'approved').length,
      rejected: biodata.filter(b => b.status === 'rejected').length,
      pending: biodata.filter(b => !['approved', 'rejected'].includes(b.status)).length
    };

    return {
      recruiterSubmissions: Object.values(recruiterStats).sort((a, b) => b.totalSubmission - a.totalSubmission),
      nationalityBreakdown: Object.values(nationalityStats).sort((a, b) => b.total - a.total),
      monthlyTrends: Object.values(monthlyStats).sort((a, b) => a.month.localeCompare(b.month)),
      statusSummary,
      totalWorkers: biodata.length
    };
  };

  const handleDataClick = async (type, data) => {
    try {
      setDetailsLoading(true);
      let biodataToShow = [];

      switch (type) {
        case 'recruiter':
          setSelectedRecruiter(data);
          biodataToShow = data.biodata;
          break;
        case 'nationality':
          setSelectedNationality(data);
          biodataToShow = data.biodata;
          break;
        case 'month':
          setSelectedMonth(data);
          biodataToShow = data.biodata;
          break;
      }

      setWorkerDetailsData(biodataToShow);
      setShowWorkerDetails(true);
    } catch (error) {
      console.error('Failed to load worker details:', error);
    } finally {
      setDetailsLoading(false);
    }
  };

  const exportToExcel = (data, filename) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');
    XLSX.writeFile(workbook, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportRecruiterReport = () => {
    const data = dashboardData.recruiterSubmissions.map(r => ({
      'Recruiter Name': r.name,
      'Country': r.country,
      'Total Submission': r.totalSubmission,
      'Approved': r.approved,
      'Rejected': r.rejected,
      'Pending': r.pending,
      'Approval Rate': r.totalSubmission > 0 ? `${Math.round((r.approved / r.totalSubmission) * 100)}%` : '0%'
    }));
    exportToExcel(data, 'Recruiter_Submission_Report');
  };

  const exportNationalityReport = () => {
    const data = dashboardData.nationalityBreakdown.map(n => ({
      'Nationality': n.nationality,
      'Total': n.total,
      'Approved': n.approved,
      'Rejected': n.rejected,
      'Pending': n.pending,
      'Approval Rate': n.total > 0 ? `${Math.round((n.approved / n.total) * 100)}%` : '0%'
    }));
    exportToExcel(data, 'Nationality_Breakdown_Report');
  };

  const exportMonthlyReport = () => {
    const data = dashboardData.monthlyTrends.map(m => ({
      'Month': m.month,
      'Total Submissions': m.total,
      'Approved': m.approved,
      'Rejected': m.rejected,
      'Pending': m.pending,
      'Approval Rate': m.total > 0 ? `${Math.round((m.approved / m.total) * 100)}%` : '0%'
    }));
    exportToExcel(data, 'Monthly_Trends_Report');
  };

  const exportWorkerDetails = () => {
    const data = workerDetailsData.map(w => ({
      'Worker ID': w.worker_id || w.id,
      'Full Name': w.full_name,
      'Nationality': w.nationality,
      'Passport Number': w.passport_number,
      'Date of Birth': w.date_of_birth ? new Date(w.date_of_birth).toLocaleDateString() : '',
      'Gender': w.gender,
      'Status': w.status,
      'Submission Date': new Date(w.created_at).toLocaleDateString(),
      'Recruiter': w.recruiters?.agency_name || 'Unknown'
    }));
    
    let filename = 'Worker_Details';
    if (selectedRecruiter) filename += `_${selectedRecruiter.name}`;
    if (selectedNationality) filename += `_${selectedNationality.nationality}`;
    if (selectedMonth) filename += `_${selectedMonth.month}`;
    
    exportToExcel(data, filename);
  };

  if (loading) {
    return (
      <AppLayout sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}>
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center space-x-3">
            <Icon name="Loader2" size={24} className="animate-spin text-primary" />
            <span className="text-lg text-text-secondary">Loading analytics...</span>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}>
      <div className="flex flex-col h-full">
        <HeaderBar 
          title="Reports & Analytics" 
          subtitle="Comprehensive biodata submission analytics and reporting"
          onMenuClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
        
        <div className="flex-1 overflow-hidden">
          <div className="h-full flex flex-col">
            <div className="p-6 border-b border-secondary-200 bg-surface">
              <BreadcrumbNavigation items={breadcrumbItems} />
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {error && (
                <div className="mb-6 p-4 bg-error-50 border border-error-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Icon name="AlertTriangle" size={20} className="text-error" />
                    <span className="text-error-800">{error}</span>
                  </div>
                </div>
              )}

              {/* Period Selector */}
              <div className="mb-6 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <h2 className="text-2xl font-bold text-text-primary">Dashboard & Reporting</h2>
                  <div className="flex items-center space-x-2">
                    <Icon name="Calendar" size={16} className="text-secondary-600" />
                    <select
                      value={selectedPeriod}
                      onChange={(e) => setSelectedPeriod(e.target.value)}
                      className="px-3 py-2 border border-secondary-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="current">Current Month</option>
                      <option value="last3months">Last 3 Months</option>
                      <option value="last6months">Last 6 Months</option>
                      <option value="lastyear">Last Year</option>
                    </select>
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="text-3xl font-bold text-primary">{dashboardData.totalWorkers}</div>
                  <div className="text-sm text-secondary-600">Total Workers</div>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="card">
                  <div className="p-6">
                    <div className="flex items-center space-x-3">
                      <div className="p-3 bg-primary-100 rounded-full">
                        <Icon name="Users" size={24} className="text-primary" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-text-primary">{dashboardData.statusSummary.total}</div>
                        <div className="text-sm text-secondary-600">Total Submissions</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="p-6">
                    <div className="flex items-center space-x-3">
                      <div className="p-3 bg-success-100 rounded-full">
                        <Icon name="CheckCircle" size={24} className="text-success" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-success">{dashboardData.statusSummary.approved}</div>
                        <div className="text-sm text-secondary-600">Approved</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="p-6">
                    <div className="flex items-center space-x-3">
                      <div className="p-3 bg-error-100 rounded-full">
                        <Icon name="XCircle" size={24} className="text-error" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-error">{dashboardData.statusSummary.rejected}</div>
                        <div className="text-sm text-secondary-600">Rejected</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="p-6">
                    <div className="flex items-center space-x-3">
                      <div className="p-3 bg-warning-100 rounded-full">
                        <Icon name="Clock" size={24} className="text-warning" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-warning">{dashboardData.statusSummary.pending}</div>
                        <div className="text-sm text-secondary-600">Pending</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tab Navigation */}
              <div className="mb-6">
                <div className="border-b border-secondary-200">
                  <nav className="flex space-x-8">
                    {[
                      { id: 'recruiter', label: 'By Recruiter Submission', icon: 'Building' },
                      { id: 'nationality', label: 'By Nationality & Monthly', icon: 'Globe' },
                      { id: 'trends', label: 'Monthly Trends', icon: 'TrendingUp' },
                      { id: 'export', label: 'Export Reports', icon: 'Download' }
                    ].map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-150 ${
                          activeTab === tab.id
                            ? 'border-primary text-primary'
                            : 'border-transparent text-secondary-600 hover:text-secondary-800 hover:border-secondary-300'
                        }`}
                      >
                        <Icon name={tab.icon} size={16} />
                        <span>{tab.label}</span>
                      </button>
                    ))}
                  </nav>
                </div>
              </div>

              {/* Tab Content */}
              {activeTab === 'recruiter' && (
                <RecruiterSubmissionTab 
                  data={dashboardData.recruiterSubmissions}
                  onDataClick={handleDataClick}
                  onExport={exportRecruiterReport}
                />
              )}

              {activeTab === 'nationality' && (
                <NationalityBreakdownTab 
                  data={dashboardData.nationalityBreakdown}
                  monthlyData={dashboardData.monthlyTrends}
                  onDataClick={handleDataClick}
                  onExport={exportNationalityReport}
                />
              )}

              {activeTab === 'trends' && (
                <MonthlyTrendsTab 
                  data={dashboardData.monthlyTrends}
                  onDataClick={handleDataClick}
                  onExport={exportMonthlyReport}
                />
              )}

              {activeTab === 'export' && (
                <ExportTab 
                  onExportRecruiter={exportRecruiterReport}
                  onExportNationality={exportNationalityReport}
                  onExportMonthly={exportMonthlyReport}
                  totalRecords={dashboardData.totalWorkers}
                />
              )}
            </div>
          </div>
        </div>

        {/* Worker Details Modal */}
        {showWorkerDetails && (
          <WorkerDetailsModal
            data={workerDetailsData}
            title={
              selectedRecruiter ? `Workers from ${selectedRecruiter.name}` :
              selectedNationality ? `Workers from ${selectedNationality.nationality}` :
              selectedMonth ? `Workers submitted in ${selectedMonth.month}` :
              'Worker Details'
            }
            loading={detailsLoading}
            onClose={() => {
              setShowWorkerDetails(false);
              setSelectedRecruiter(null);
              setSelectedNationality(null);
              setSelectedMonth(null);
              setWorkerDetailsData([]);
            }}
            onExport={exportWorkerDetails}
          />
        )}
      </div>
    </AppLayout>
  );
};

// Recruiter Submission Tab Component
const RecruiterSubmissionTab = ({ data, onDataClick, onExport }) => (
  <div className="card">
    <div className="p-6 border-b border-secondary-200">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-text-primary flex items-center space-x-2">
          <Icon name="Building" size={20} className="text-primary" />
          <span>Recruiter Submission Analytics</span>
        </h3>
        <button
          onClick={onExport}
          className="flex items-center space-x-2 px-4 py-2 bg-success text-white rounded-md hover:bg-success-700 transition-colors duration-150"
        >
          <Icon name="Download" size={16} />
          <span>Export Excel</span>
        </button>
      </div>
    </div>
    
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-secondary-50 border-b border-secondary-200">
          <tr>
            <th className="px-6 py-4 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">Recruiter</th>
            <th className="px-6 py-4 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">Country</th>
            <th className="px-6 py-4 text-center text-xs font-medium text-secondary-700 uppercase tracking-wider">Total Submission</th>
            <th className="px-6 py-4 text-center text-xs font-medium text-secondary-700 uppercase tracking-wider">Approved</th>
            <th className="px-6 py-4 text-center text-xs font-medium text-secondary-700 uppercase tracking-wider">Rejected</th>
            <th className="px-6 py-4 text-center text-xs font-medium text-secondary-700 uppercase tracking-wider">Pending</th>
            <th className="px-6 py-4 text-center text-xs font-medium text-secondary-700 uppercase tracking-wider">Approval Rate</th>
          </tr>
        </thead>
        <tbody className="bg-surface divide-y divide-secondary-200">
          {data.map((recruiter, index) => (
            <tr 
              key={recruiter.id} 
              className="hover:bg-secondary-50 cursor-pointer transition-colors duration-150"
              onClick={() => onDataClick('recruiter', recruiter)}
            >
              <td className="px-6 py-4">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-8 h-8 bg-primary-100 text-primary-800 rounded-full text-sm font-medium">
                    {index + 1}
                  </div>
                  <div>
                    <div className="font-medium text-text-primary">{recruiter.name}</div>
                    <div className="text-sm text-secondary-600">Click to view workers</div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-800">
                  {recruiter.country}
                </span>
              </td>
              <td className="px-6 py-4 text-center">
                <span className="text-2xl font-bold text-primary">{recruiter.totalSubmission}</span>
              </td>
              <td className="px-6 py-4 text-center">
                <span className="text-lg font-semibold text-success">{recruiter.approved}</span>
              </td>
              <td className="px-6 py-4 text-center">
                <span className="text-lg font-semibold text-error">{recruiter.rejected}</span>
              </td>
              <td className="px-6 py-4 text-center">
                <span className="text-lg font-semibold text-warning">{recruiter.pending}</span>
              </td>
              <td className="px-6 py-4 text-center">
                <div className="flex flex-col items-center">
                  <span className="text-lg font-bold text-accent">
                    {recruiter.totalSubmission > 0 ? Math.round((recruiter.approved / recruiter.totalSubmission) * 100) : 0}%
                  </span>
                  <div className="w-16 bg-secondary-200 rounded-full h-2 mt-1">
                    <div 
                      className="bg-accent h-2 rounded-full transition-all duration-500"
                      style={{ 
                        width: `${recruiter.totalSubmission > 0 ? (recruiter.approved / recruiter.totalSubmission) * 100 : 0}%` 
                      }}
                    ></div>
                  </div>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Monthly Trends Tab Component
const MonthlyTrendsTab = ({ data, onDataClick, onExport }) => (
  <div className="card">
    <div className="p-6 border-b border-secondary-200">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-text-primary flex items-center space-x-2">
          <Icon name="TrendingUp" size={20} className="text-accent" />
          <span>Monthly Submission Trends</span>
        </h3>
        <button
          onClick={onExport}
          className="flex items-center space-x-2 px-4 py-2 bg-success text-white rounded-md hover:bg-success-700 transition-colors duration-150"
        >
          <Icon name="Download" size={16} />
          <span>Export Excel</span>
        </button>
      </div>
    </div>
    
    <div className="p-6">
      {/* Trend Chart Visualization */}
      <div className="mb-8 p-6 bg-gradient-to-r from-accent-50 to-primary-50 rounded-lg border border-accent-200">
        <h4 className="font-semibold text-accent-800 mb-4">Submission Trend Analysis</h4>
        <div className="space-y-4">
          {data.map((month, index) => {
            const maxValue = Math.max(...data.map(m => m.total));
            const percentage = maxValue > 0 ? (month.total / maxValue) * 100 : 0;
            
            return (
              <div key={month.month} className="flex items-center space-x-4">
                <div className="w-20 text-sm font-medium text-accent-800">{month.month}</div>
                <div className="flex-1 bg-secondary-200 rounded-full h-8 relative">
                  <div 
                    className="bg-gradient-to-r from-accent to-primary h-8 rounded-full transition-all duration-1000 flex items-center justify-end pr-3"
                    style={{ width: `${Math.max(percentage, 5)}%` }}
                  >
                    <span className="text-white text-sm font-medium">{month.total}</span>
                  </div>
                </div>
                <div className="w-16 text-sm text-secondary-600">{percentage.toFixed(1)}%</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Detailed Monthly Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-secondary-50 border-b border-secondary-200">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">Month</th>
              <th className="px-6 py-4 text-center text-xs font-medium text-secondary-700 uppercase tracking-wider">Total Submissions</th>
              <th className="px-6 py-4 text-center text-xs font-medium text-secondary-700 uppercase tracking-wider">Approved</th>
              <th className="px-6 py-4 text-center text-xs font-medium text-secondary-700 uppercase tracking-wider">Rejected</th>
              <th className="px-6 py-4 text-center text-xs font-medium text-secondary-700 uppercase tracking-wider">Pending</th>
              <th className="px-6 py-4 text-center text-xs font-medium text-secondary-700 uppercase tracking-wider">Success Rate</th>
              <th className="px-6 py-4 text-center text-xs font-medium text-secondary-700 uppercase tracking-wider">Growth</th>
            </tr>
          </thead>
          <tbody className="bg-surface divide-y divide-secondary-200">
            {data.map((month, index) => {
              const previousMonth = index > 0 ? data[index - 1] : null;
              const growth = previousMonth ? ((month.total - previousMonth.total) / previousMonth.total) * 100 : 0;
              const successRate = month.total > 0 ? (month.approved / month.total) * 100 : 0;
              
              return (
                <tr 
                  key={month.month} 
                  className="hover:bg-secondary-50 cursor-pointer transition-colors duration-150"
                  onClick={() => onDataClick('month', month)}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <Icon name="Calendar" size={16} className="text-accent" />
                      <div>
                        <div className="font-medium text-text-primary">{month.month}</div>
                        <div className="text-sm text-secondary-600">Click to view workers</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-2xl font-bold text-primary">{month.total}</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-lg font-semibold text-success">{month.approved}</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-lg font-semibold text-error">{month.rejected}</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-lg font-semibold text-warning">{month.pending}</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex flex-col items-center">
                      <span className="text-lg font-bold text-accent">{successRate.toFixed(1)}%</span>
                      <div className="w-16 bg-secondary-200 rounded-full h-2 mt-1">
                        <div 
                          className="bg-accent h-2 rounded-full transition-all duration-500"
                          style={{ width: `${successRate}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center space-x-1">
                      <Icon 
                        name={growth > 0 ? 'TrendingUp' : growth < 0 ? 'TrendingDown' : 'Minus'} 
                        size={16} 
                        className={growth > 0 ? 'text-success' : growth < 0 ? 'text-error' : 'text-secondary-400'} 
                      />
                      <span className={`font-semibold ${growth > 0 ? 'text-success' : growth < 0 ? 'text-error' : 'text-secondary-400'}`}>
                        {previousMonth ? `${growth > 0 ? '+' : ''}${growth.toFixed(1)}%` : 'N/A'}
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

// Export Tab Component
const ExportTab = ({ onExportRecruiter, onExportNationality, onExportMonthly, totalRecords }) => (
  <div className="space-y-6">
    <div className="card">
      <div className="p-6 border-b border-secondary-200">
        <h3 className="text-lg font-semibold text-text-primary flex items-center space-x-2">
          <Icon name="Download" size={20} className="text-success" />
          <span>Export Reports</span>
        </h3>
        <p className="text-sm text-secondary-600 mt-1">Download comprehensive reports in Excel format</p>
      </div>
      
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Recruiter Report */}
          <div className="border border-secondary-200 rounded-lg p-6 hover:border-primary-300 transition-colors duration-150">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-3 bg-primary-100 rounded-full">
                <Icon name="Building" size={24} className="text-primary" />
              </div>
              <div>
                <h4 className="font-semibold text-text-primary">Recruiter Submission Report</h4>
                <p className="text-sm text-secondary-600">By agency performance</p>
              </div>
            </div>
            <div className="mb-4">
              <div className="text-sm text-secondary-600">Includes:</div>
              <ul className="text-sm text-secondary-600 mt-1 space-y-1">
                <li>• Agency name and country</li>
                <li>• Total submissions</li>
                <li>• Approval/rejection breakdown</li>
                <li>• Performance metrics</li>
              </ul>
            </div>
            <button
              onClick={onExportRecruiter}
              className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-700 transition-colors duration-150"
            >
              <Icon name="Download" size={16} />
              <span>Export Recruiter Report</span>
            </button>
          </div>

          {/* Nationality Report */}
          <div className="border border-secondary-200 rounded-lg p-6 hover:border-accent-300 transition-colors duration-150">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-3 bg-accent-100 rounded-full">
                <Icon name="Globe" size={24} className="text-accent" />
              </div>
              <div>
                <h4 className="font-semibold text-text-primary">Nationality Breakdown Report</h4>
                <p className="text-sm text-secondary-600">By worker nationality</p>
              </div>
            </div>
            <div className="mb-4">
              <div className="text-sm text-secondary-600">Includes:</div>
              <ul className="text-sm text-secondary-600 mt-1 space-y-1">
                <li>• Nationality distribution</li>
                <li>• Status breakdown</li>
                <li>• Approval rates by country</li>
                <li>• Demographic insights</li>
              </ul>
            </div>
            <button
              onClick={onExportNationality}
              className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-accent text-white rounded-md hover:bg-accent-700 transition-colors duration-150"
            >
              <Icon name="Download" size={16} />
              <span>Export Nationality Report</span>
            </button>
          </div>

          {/* Monthly Trends Report */}
          <div className="border border-secondary-200 rounded-lg p-6 hover:border-success-300 transition-colors duration-150">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-3 bg-success-100 rounded-full">
                <Icon name="TrendingUp" size={24} className="text-success" />
              </div>
              <div>
                <h4 className="font-semibold text-text-primary">Monthly Trends Report</h4>
                <p className="text-sm text-secondary-600">Time-based analysis</p>
              </div>
            </div>
            <div className="mb-4">
              <div className="text-sm text-secondary-600">Includes:</div>
              <ul className="text-sm text-secondary-600 mt-1 space-y-1">
                <li>• Monthly submission trends</li>
                <li>• Growth rate analysis</li>
                <li>• Seasonal patterns</li>
                <li>• Performance forecasting</li>
              </ul>
            </div>
            <button
              onClick={onExportMonthly}
              className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-success text-white rounded-md hover:bg-success-700 transition-colors duration-150"
            >
              <Icon name="Download" size={16} />
              <span>Export Monthly Report</span>
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="mt-8 p-6 bg-gradient-to-r from-primary-50 to-accent-50 border border-primary-200 rounded-lg">
          <div className="flex items-center space-x-3 mb-4">
            <Icon name="Info" size={20} className="text-primary" />
            <h4 className="font-semibold text-primary-800">Export Information</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="font-medium text-primary-800">Total Records Available</div>
              <div className="text-primary-700">{totalRecords} worker records</div>
            </div>
            <div>
              <div className="font-medium text-primary-800">Export Format</div>
              <div className="text-primary-700">Microsoft Excel (.xlsx)</div>
            </div>
            <div>
              <div className="font-medium text-primary-800">Data Freshness</div>
              <div className="text-primary-700">Real-time data</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default ClabReportsAnalytics; 